/**
 * Initialize the "Continue with Facebook" button on the current step:
 * the email step's (Facebook arms only), or the password step's, which a
 * Facebook-only account's submit error reveals in every arm and which
 * carries `data-facebook-auth-submits`.
 *
 * Wires the button to open the Facebook OAuth flow in a popup window
 * and listens for the postMessage result sent by the popup's callback
 * page. Called with `this` bound to the QuoteRequestForm instance.
 *
 * @name initializeFacebookAuth
 */
function initializeFacebookAuth() {
	const facebookAuthButton = this.getFacebookAuthButton();

	if (!facebookAuthButton) {
		return;
	}

	const submitsQuoteRequest = 'facebookAuthSubmits' in facebookAuthButton.dataset;
	const placement = facebookAuthButton.dataset.facebookAuthPlacement;
	const fallbackAdvice = submitsQuoteRequest ? 'try again' : 'continue with your email instead';

	// The popup must be opened synchronously within the click handler,
	// otherwise popup blockers will reject the window.open call.
	const handleFacebookAuthClick = () => {
		const popup = window.open(facebookAuthButton.dataset.facebookAuthUrl, 'gs-facebook-auth', 'popup,width=600,height=700');

		if (!popup) {
			this.showStepErrors([new Error(`We couldn’t open the Facebook sign in window. Please allow popups for this site, or ${fallbackAdvice}.`)]);
			return;
		}

		// Reset per attempt: this click begins a fresh popup round trip.
		this._facebookAuthMessageReceived = false;

		this.trackEvent('facebook_auth_started', { placement });

		// Watchdog: if the popup closes without ever posting a result
		// (a server error page, an origin mismatch dropping the
		// postMessage, a concurrent flow consuming the session's SSO
		// intent, or the user simply closing it), the opener would
		// otherwise get zero feedback. The session cookie is shared, so
		// probe the next-steps endpoint and let the response tell us
		// whether the popup login actually happened.
		this.clearFacebookAuthWatchdog();

		// No watchdog on the password step: it is the last step, so a
		// next-steps probe can't show whether the login happened, and a
		// signed-in planner's resubmit already skips the password check.
		if (submitsQuoteRequest) {
			return;
		}

		this._facebookAuthWatchdog = setInterval(() => {
			if (!popup.closed) {
				return;
			}

			this.clearFacebookAuthWatchdog();

			// Stay conservative once the user has moved past the email
			// step — later step fetches reconcile the session on their
			// own.
			if (!this.getFacebookAuthButton()) {
				return;
			}

			// The popup's own message is authoritative; if one already landed
			// there is nothing to reconcile and a second fetch would race it.
			if (this._facebookAuthMessageReceived) {
				return;
			}

			// Deliberately no ladda here: the silent no-op path below
			// would leave it spinning.
			const probedStepElement = this.currentStepElement;

			this.loadNextSteps(error => {
				if (error) {
					return;
				}

				const nextStepElement = this.getAdjacentStepElement(this.currentStepElement, 'next');

				// Credential steps are composed only for anonymous sessions,
				// so one coming next means the login didn't happen: stay put,
				// silently. The exception is the OTP step an authenticated
				// planner in the OTP cohort also gets; its
				// otp_use_account_email flag is a positive login signal. An
				// empty plan is impossible for an anonymous session, so it
				// proves a signed-in account with a complete planner profile.
				if (nextStepElement) {
					const isCredentialStep = ['event_planner_password', 'one_time_verification_code'].includes(nextStepElement.dataset.stepName);
					const isAccountOtpStep = nextStepElement.querySelector('input[name="otp_use_account_email"]') !== null;

					if (isCredentialStep && !isAccountOtpStep) {
						return;
					}
				}

				this.advancePastEmailStep(this.currentStepElement);
			}, {
				probe: true,
				// The popup closes right after posting, so `popup.closed` can be
				// seen before its message arrives: a message that landed while
				// this probe was in flight owns the success, and a planner who
				// has moved to another step must not have steps archived or
				// inserted around it.
				isStale: () => this._facebookAuthMessageReceived || this.currentStepElement !== probedStepElement,
			});
		}, 500);
	};

	// Remove any prior listener to prevent duplicates on re-init.
	facebookAuthButton.removeEventListener('click', facebookAuthButton._facebookAuthClickHandler);
	facebookAuthButton.addEventListener('click', handleFacebookAuthClick);
	facebookAuthButton._facebookAuthClickHandler = handleFacebookAuthClick;

	// Keep focus in the step's input for the whole click: Safari and
	// Firefox on macOS never focus buttons on mousedown, so the form's
	// focusout validator would fire mid-click, inject a required-field
	// error above the button, and shift it out from under the pointer.
	// Preventing mousedown default stops the focus change while the
	// click still dispatches; the focusout relatedTarget guard covers
	// keyboard Tab navigation.
	const preventFocusSteal = event => event.preventDefault();
	facebookAuthButton.removeEventListener('mousedown', facebookAuthButton._facebookAuthMousedownHandler);
	facebookAuthButton.addEventListener('mousedown', preventFocusSteal);
	facebookAuthButton._facebookAuthMousedownHandler = preventFocusSteal;

	const handleFacebookAuthMessage = event => {
		// Only accept messages posted by our own SSO popup callback
		// page for the quote request flow.
		if (event.origin !== window.location.origin) {
			return;
		}

		const data = event.data;
		if (!data || data.source !== 'gs-sso' || data.intent !== 'quote-request') {
			return;
		}

		// A real result from the popup always supersedes the
		// popup-closed watchdog, even when we go on to ignore it below. The flag
		// also stands down a probe that is ALREADY in flight — clearing the
		// interval cannot cancel its fetch.
		this._facebookAuthMessageReceived = true;
		this.clearFacebookAuthWatchdog();

		// A late result (user abandoned the popup and kept going) must not
		// error, archive or submit from an unrelated step — only act while a
		// step with a visible Facebook button is still the current step.
		const currentFacebookAuthButton = this.getFacebookAuthButton();
		if (!currentFacebookAuthButton) {
			return;
		}

		const currentPlacement = currentFacebookAuthButton.dataset.facebookAuthPlacement;

		// Statuses mirror App\Enums\SsoPopupStatus: success, error, cancelled.
		if (data.status === 'success') {
			this.trackEvent('facebook_auth_success', { placement: currentPlacement });

			if ('facebookAuthSubmits' in currentFacebookAuthButton.dataset) {
				this.submitAfterFacebookAuth(data.email);
			} else {
				this.resumeAfterFacebookAuth(data.email);
			}
		} else if (data.status === 'cancelled') {
			// Declining Facebook's dialog leaves the step as it was: no
			// error, and no Bugsnag notice.
			this.trackEvent('facebook_auth_cancelled', { placement: currentPlacement });
		} else {
			this.showStepErrors([new Error(data.message || 'We couldn’t sign you in with Facebook. Please try again.')]);
		}
	};

	// Keep a single message listener per wizard instance so a
	// re-rendered email step never stacks duplicate listeners.
	window.removeEventListener('message', this._facebookAuthMessageHandler);
	window.addEventListener('message', handleFacebookAuthMessage);
	this._facebookAuthMessageHandler = handleFacebookAuthMessage;
}

export {
	initializeFacebookAuth
};
